import React, { useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { API_BASE } from '../../lib/config';

// API_BASE は常に "/api" を含むため、そのまま利用する
const API = API_BASE;

const CreateJob = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    hourly_rate: '',
    work_hours: '',
    work_location: '',
    prefecture: '',
    city: '',
    category: '',
    requirements: '',
    benefits: '',
    transport_type: 'none',
    transport_amount: '',
    rounding_minutes: 15,
    items_to_bring: [],
    dress_code: '',
    access_info: '',
    building: ''
  });


  const [updatedItems, setUpdatedItems] = useState([]); // unused but keeping to avoid renames issues if any
  const [newItem, setNewItem] = useState(''); // For adding items_to_bring
  const [categories, setCategories] = useState([]);

  // Fetch categories on mount
  React.useEffect(() => {
    const fetchCats = async () => {
      try {
        const res = await fetch(`${API_BASE}/categories`);
        if (res.ok) {
          const data = await res.json();
          // Adjust based on if data is { categories: [] } or just []
          // public endpoints typically return list now based on my last fix
          const list = Array.isArray(data) ? data : (data.categories || []);
          setCategories(list);
        }
      } catch (err) {
        console.error("Failed to load categories", err);
      }
    };
    fetchCats();
  }, []);


  // 画像/動画アップロード用の状態
  const [mediaFiles, setMediaFiles] = useState([]); // File[]
  const [mediaPreviews, setMediaPreviews] = useState([]); // {url, type}[]

  const handleMediaChange = (e) => {
    const files = Array.from(e.target.files || []);
    setMediaFiles(files);
    const previews = files.map(f => ({ url: URL.createObjectURL(f), type: f.type.startsWith('video') ? 'video' : 'image' }));
    setMediaPreviews(previews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      // 1) メディアを先にアップロード
      let mediaPayload = [];
      if (mediaFiles.length > 0) {
        const fd = new FormData();
        mediaFiles.forEach((f) => fd.append('files', f));
        const uploadResp = await fetch(`${API}/media/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: fd
        });
        if (!uploadResp.ok) {
          const t = await uploadResp.text();
          throw new Error(`メディアアップロードに失敗しました: ${uploadResp.status} ${t}`);
        }
        const uploaded = await uploadResp.json(); // { files: [{url,type}] }
        mediaPayload = uploaded.files || [];
      }

      // 2) 求人本体を投稿（バックエンドの JobCreate に合わせて images/videos を分離）
      const imageUrls = mediaPayload.filter(m => (m.type || '').startsWith('image')).map(m => m.url);
      const videoUrls = mediaPayload.filter(m => (m.type || '').startsWith('video')).map(m => m.url);
      // Backend JobCreate に必須のフィールドへマッピング
      const now = new Date();
      const durationHours = 8; // フォーム未入力のため暫定8時間
      const startISO = now.toISOString();
      const endISO = new Date(now.getTime() + durationHours * 60 * 60 * 1000).toISOString();

      // 3) 住所から緯度経度を取得 (Google Maps Geocoding API)
      let lat = null;
      let lng = null;
      try {
        const { geocodeAddress } = require('../../utils/location');
        const keyRes = await fetch(`${API}/api-keys/maps`);
        if (keyRes.ok) {
          const keyData = await keyRes.json();
          const googleKey = keyData.google_maps_api_key;
          if (googleKey) {
            const fullAddress = `${formData.prefecture || ''}${formData.city || ''}${formData.work_location || ''}`;
            const coords = await geocodeAddress(fullAddress, googleKey);
            lat = coords.lat;
            lng = coords.lng;
          }
        }
      } catch (geoErr) {
        console.warn("Geocoding failed:", geoErr);
      }

      const response = await fetch(`${API}/jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          // 必須フィールド
          title: formData.title,
          description: formData.description,
          category: formData.category || 'その他',
          hourly_rate: Number(formData.hourly_rate || 0),
          duration_hours: durationHours,
          start_date: startISO,
          end_date: endISO,
          location: `${formData.prefecture}${formData.city}${formData.work_location}`,
          prefecture: formData.prefecture || '未設定',
          city: formData.city || '未設定',
          address: formData.work_location || '未設定',
          building: formData.building,
          latitude: lat,
          longitude: lng,
          access_info: formData.access_info,
          requirements: [],
          images: imageUrls,
          videos: videoUrls,
          // サムネイルは最初の画像をデフォルトに
          thumbnail_image: imageUrls.length > 0 ? imageUrls[0] : null,
          // 参考: オプション項目
          work_hours: formData.work_hours || '',
          required_people: 1,
          items_to_bring: formData.items_to_bring,
          dress_code: formData.dress_code,
          experience_required: false,
          same_day_ok: false,
          max_applicants: 1,
          publication_start_date: null,
          publication_end_date: null,
          tags: [],
          // 支払ルール
          rounding_minutes: Number(formData.rounding_minutes || 15),
          transport_type: formData.transport_type || 'none',
          transport_amount: Number(formData.transport_amount || 0),
        })
      });

      if (response.ok) {
        toast.success('求人を投稿しました');
        navigate('/employer-dashboard');
      } else {
        const t = await response.text();
        toast.error(`求人投稿に失敗しました: ${response.status} ${t}`);
      }
    } catch (error) {
      console.error(error);
      toast.error('エラーが発生しました');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/employer-dashboard')}
                className="mr-4 p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">求人投稿</h1>
            </div>
          </div>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  求人タイトル
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  例: 【急募】土日ランチ・カフェホールスタッフ募集！ (30文字以内で魅力的に)
                </p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    仕事内容
                  </label>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, description: "【業務内容】\n・ホールでの接客\n・簡単な調理補助\n\n【職場の雰囲気】\n20代のスタッフが中心に活躍中！未経験でも優しく教えます。\n\n【注意事項】\n・ネイル、ピアスは不可となります。\n・開始10分前には集合してください。" })}
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    テンプレートを入力
                  </button>
                </div>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="6"
                  placeholder="具体的な業務内容や、働く環境について詳しく記載してください。"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  ※具体的に書くことで、マッチング率が向上します。
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    時給（円）
                  </label>
                  <input
                    type="number"
                    name="hourly_rate"
                    value={formData.hourly_rate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    勤務時間
                  </label>
                  <input
                    type="text"
                    name="work_hours"
                    value={formData.work_hours}
                    onChange={handleChange}
                    placeholder="例: 09:00〜17:00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              {/* 支払ルール */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    交通費
                  </label>
                  <div className="flex gap-2">
                    <select
                      name="transport_type"
                      value={formData.transport_type}
                      onChange={handleChange}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="none">なし</option>
                      <option value="fixed">定額</option>
                    </select>
                    <input
                      type="number"
                      name="transport_amount"
                      value={formData.transport_amount}
                      onChange={handleChange}
                      placeholder="金額（円）"
                      disabled={formData.transport_type !== 'fixed'}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formData.transport_type !== 'fixed' ? 'bg-gray-100 border-gray-200' : 'border-gray-300'}`}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    丸め（勤怠計算）
                  </label>
                  <input
                    type="text"
                    value={`15分単位（固定）`}
                    disabled
                    className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-100 text-gray-600"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  勤務地 (住所)
                </label>
                <div className="grid grid-cols-2 gap-4 mb-2">
                  <input
                    type="text"
                    name="prefecture"
                    value={formData.prefecture}
                    onChange={handleChange}
                    placeholder="都道府県 (例: 東京都)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="市区町村 (例: 渋谷区)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <input
                  type="text"
                  name="work_location"
                  value={formData.work_location}
                  onChange={handleChange}
                  placeholder="番地・詳細 (例: 神南1-1-1)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                  required
                />
                <input
                  type="text"
                  name="building"
                  value={formData.building}
                  onChange={handleChange}
                  placeholder="建物名・階数 (例: 〇〇ビル 3F)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                />
                <textarea
                  name="access_info"
                  value={formData.access_info}
                  onChange={handleChange}
                  rows="2"
                  placeholder="アクセス詳細 (例: 東口を出て徒歩5分、コンビニの角を右)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    持ち物 (クリックで追加)
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {['ボールペン', 'メモ帳', '身分証明書', '印鑑', '黒ズボン', 'スニーカー', '腕時計(秒針付)'].map(s => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => {
                          if (!formData.items_to_bring.includes(s)) {
                            setFormData({ ...formData, items_to_bring: [...formData.items_to_bring, s] })
                          }
                        }}
                        className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded hover:bg-gray-200"
                      >
                        + {s}
                      </button>
                    ))}
                  </div>
                  <div className="flex space-x-2 mb-2">
                    <input
                      type="text"
                      value={newItem}
                      onChange={(e) => setNewItem(e.target.value)}
                      placeholder="例：ボールペン、メモ帳"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (newItem.trim()) {
                          setFormData({ ...formData, items_to_bring: [...formData.items_to_bring, newItem.trim()] });
                          setNewItem('');
                        }
                      }}
                      className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                    >
                      追加
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.items_to_bring.map((item, idx) => (
                      <span key={idx} className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-sm flex items-center">
                        {item}
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, items_to_bring: formData.items_to_bring.filter((_, i) => i !== idx) })}
                          className="ml-1 text-blue-400 hover:text-blue-600"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    服装・髪型
                  </label>
                  <textarea
                    name="dress_code"
                    value={formData.dress_code}
                    onChange={handleChange}
                    rows="3"
                    placeholder="例：動きやすい服装、ジーンズNG、髪色自由"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    {['髪色自由', 'ネイルOK', 'ピアスOK', 'ひげOK', '制服貸与', 'ジーンズNG', 'サンダルNG'].map(s => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => {
                          const current = formData.dress_code || "";
                          if (!current.includes(s)) {
                            setFormData({ ...formData, dress_code: current ? current + "、" + s : s });
                          }
                        }}
                        className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded hover:bg-gray-200"
                      >
                        + {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  カテゴリ
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">カテゴリを選択</option>
                  {categories.length > 0 ? (
                    categories.map((cat) => (
                      <option key={cat.id || cat.name} value={cat.label || cat.name}>
                        {cat.label || cat.name}
                      </option>
                    ))
                  ) : (
                    // Fallback if fetch failed or loading
                    <>
                      <option value="飲食・フード">飲食・フード</option>
                      <option value="販売・接客">販売・接客</option>
                      <option value="イベント・キャンペーン">イベント・キャンペーン</option>
                      <option value="軽作業・倉庫">軽作業・倉庫</option>
                      <option value="配送・デリバリー">配送・デリバリー</option>
                      <option value="オフィスワーク">オフィスワーク</option>
                      <option value="清掃・クリーニング">清掃・クリーニング</option>
                      <option value="モニター・調査">モニター・調査</option>
                      <option value="介護・保育">介護・保育</option>
                      <option value="その他">その他</option>
                    </>
                  )}
                </select>
              </div>

              {/* メディア（画像・動画）アップロード */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  画像・動画（最大10ファイル、合計100MB）
                </label>

                {/* Image Guidance Component */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                  <h4 className="text-sm font-bold text-blue-800 mb-2">📸 魅力的な求人にするためのポイント</h4>
                  <ul className="text-xs text-blue-700 list-disc list-inside space-y-1">
                    <li><strong>推奨サイズ:</strong> 画像 1200×900px (4:3)、動画 1280×720px (16:9)</li>
                    <li><strong>枚数:</strong> 3枚以上の登録を推奨します（最大10枚）</li>
                    <li><strong>掲載する画像の例:</strong>
                      <span className="ml-1 text-blue-600">店舗の外観、店内の雰囲気、制服を着たスタッフ、実際の作業風景など</span>
                    </li>
                  </ul>
                </div>

                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleMediaChange}
                  className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-l-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {mediaPreviews.length > 0 && (
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                    {mediaPreviews.map((m, idx) => (
                      <div key={idx} className="relative border rounded overflow-hidden">
                        {m.type === 'video' ? (
                          <div className="w-full h-40 sm:h-44 md:h-36 lg:h-40 bg-black">
                            <video
                              src={m.url}
                              className="w-full h-full object-cover"
                              controls
                            />
                          </div>
                        ) : (
                          <div className="w-full h-40 sm:h-44 md:h-36 lg:h-40 bg-gray-100">
                            <img
                              src={m.url}
                              alt="preview"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => navigate('/employer-dashboard')}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  求人を投稿
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateJob;